import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { SecondaryButton } from "./AdminUI";
import { Check, X } from "lucide-react";

function formatPreview(value) {
  if (value === null || value === undefined || value === "") return <span className="italic text-[#6C757D]">empty</span>;
  if (typeof value === "string" && /^https?:\/\//.test(value) && /\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(value)) {
    return <img src={value} alt="" className="max-h-24 rounded border border-[#E9ECEF]" />;
  }
  if (typeof value === "string" && /^https?:\/\//.test(value)) {
    return <a href={value} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline break-all">{value}</a>;
  }
  return <span className="whitespace-pre-wrap text-sm">{String(value)}</span>;
}

/**
 * Props:
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 *  - diffs: [{ key, label, oldValue, newValue }]  // parent should reset this between opens
 *  - onApproveField: (key, newValue) => void  // called immediately on Approve click
 *  - onAllResolved: () => void  // called when last row is resolved (parent typically clears diffs)
 */
export function RecrawlDiffModal({ open, onOpenChange, diffs, onApproveField, onAllResolved, title }) {
  // Track which row keys have been resolved (approved or discarded) within this open session
  const [resolved, setResolved] = useState(new Set());

  const visible = (diffs || []).filter((d) => !resolved.has(d.key));

  function resolve(key) {
    const next = new Set(resolved);
    next.add(key);
    setResolved(next);
    if ((diffs || []).every((d) => next.has(d.key))) {
      setResolved(new Set());
      onAllResolved?.();
      onOpenChange(false);
    }
  }

  function approve(d) {
    onApproveField(d.key, d.newValue);
    resolve(d.key);
  }

  function discard(d) {
    resolve(d.key);
  }

  function close() {
    setResolved(new Set());
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}
    >
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="recrawl-diff-modal">
        <DialogHeader>
          <DialogTitle>{title || "Recrawl results — review changes"}</DialogTitle>
          <DialogDescription>
            For each field that changed, click <strong>Approve</strong> to write the new value into the form, or <strong>Discard</strong> to keep the current one.
            You&apos;ll still need to click <strong>Save</strong> on the form to persist the approved changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {visible.map((d) => (
            <div
              key={d.key}
              className="border border-[#E9ECEF] rounded-md p-3 bg-white"
              data-testid={`diff-row-${d.key}`}
            >
              <div className="flex items-center justify-between mb-2 gap-3">
                <div className="text-sm font-medium">{d.label}</div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => approve(d)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs font-medium bg-leaf text-white border-leaf hover:bg-leaf/90"
                    data-testid={`approve-${d.key}`}
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => discard(d)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs font-medium bg-white text-clay border-clay hover:bg-clay/5"
                    data-testid={`discard-${d.key}`}
                  >
                    <X className="w-3.5 h-3.5" /> Discard
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Current</div>
                  <div className="text-sm">{formatPreview(d.oldValue)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">New (from recrawl)</div>
                  <div className="text-sm">{formatPreview(d.newValue)}</div>
                </div>
              </div>
            </div>
          ))}
          {visible.length === 0 && (
            <div className="py-8 text-center text-sm text-[#6C757D]">No changes left to review.</div>
          )}
        </div>

        <DialogFooter>
          <SecondaryButton onClick={close} data-testid="recrawl-diff-close">Close</SecondaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
